import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Search, AlertTriangle, Package, Boxes, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Trash2, Percent } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

export default function Products() {
  const { user } = useAuth();
  const canEdit = ["admin", "manager", "inventory"].includes(user?.role);
  const canDelete = ["admin", "manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [low, setLow] = useState(false);
  const [lowMargin, setLowMargin] = useState(false);
  const [marginThreshold, setMarginThreshold] = useState(15);
  const [marginDraft, setMarginDraft] = useState(15);
  const [marginBusy, setMarginBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [batchFor, setBatchFor] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = () => api.get("/products", { params: { q: debouncedQ, low_stock: low, low_margin: lowMargin } }).then(r => setItems(r.data));
  useEffect(() => { load(); }, [debouncedQ, low, lowMargin]); // eslint-disable-line
  useEffect(() => {
    api.get("/brands").then(r => setBrands(r.data));
    api.get("/categories").then(r => setCats(r.data));
    api.get("/settings/margin").then(r => {
      setMarginThreshold(r.data.alert_threshold_percent);
      setMarginDraft(r.data.alert_threshold_percent);
    });
  }, []);

  const saveMarginThreshold = async () => {
    setMarginBusy(true);
    try {
      const { data } = await api.patch("/settings/margin", null, { params: { alert_threshold_percent: marginDraft } });
      setMarginThreshold(data.alert_threshold_percent);
      toast.success("Margin alert threshold updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setMarginBusy(false); }
  };

  const save = async (form) => {
    try {
      if (edit) await api.patch(`/products/${edit.id}`, form);
      else await api.post("/products", form);
      toast.success("Saved");
      setOpen(false); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This also removes all its batches and stock history. This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="products-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Inventory</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Products</h1>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-products-btn">
              <Upload className="w-4 h-4 mr-1" />Import from Excel
            </Button>
            <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" />Add product</Button>
          </div>
        )}
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search by name, SKU, barcode…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="products-search" />
          </div>
          <Button variant={low ? "default" : "outline"} onClick={() => setLow(v => !v)} data-testid="filter-low-stock">
            <AlertTriangle className="w-4 h-4 mr-1" /> Low stock
          </Button>
          <Button variant={lowMargin ? "default" : "outline"} onClick={() => setLowMargin(v => !v)} data-testid="filter-low-margin">
            <Percent className="w-4 h-4 mr-1" /> Low margin
          </Button>
          {canEdit && (
            <div className="flex items-center gap-1.5 ml-1" data-testid="margin-threshold-inline">
              <span className="text-xs text-muted-foreground">Alert &lt;</span>
              <Input
                type="number" min={0} max={100} step={0.5}
                value={marginDraft}
                onChange={e => setMarginDraft(parseFloat(e.target.value) || 0)}
                className="h-9 w-16 text-center"
                data-testid="margin-threshold-input"
              />
              <span className="text-xs text-muted-foreground">%</span>
              {marginDraft !== marginThreshold && (
                <Button size="sm" variant="outline" onClick={saveMarginThreshold} disabled={marginBusy} data-testid="margin-threshold-save">
                  Save
                </Button>
              )}
            </div>
          )}
        </div>
        <Table className="mt-3">
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Barcode / SKU</TableHead>
              <TableHead>HSN · GST</TableHead>
              <TableHead className="text-right">Price / Cost</TableHead>
              <TableHead className="text-right">Margin</TableHead>
              <TableHead className="text-right">Stock</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(p => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{p.barcode || p.sku || "—"}</TableCell>
                <TableCell className="text-xs">{p.hsn_code || "—"} · {p.gst_percent}%</TableCell>
                <TableCell className="text-right font-mono text-sm">
                  ₹{p.sale_price}
                  {p.cost_price > 0 && <span className="block text-[10px] text-muted-foreground">cost ₹{p.cost_price}</span>}
                </TableCell>
                <TableCell className="text-right">
                  {p.margin_percent === null ? (
                    <span className="text-xs text-muted-foreground" title="Set cost price to track margin">—</span>
                  ) : p.margin_alert ? (
                    <Badge variant="destructive" data-testid={`product-margin-alert-${p.id}`} title={`Below alert threshold (${p.margin_threshold_percent}%)`}>
                      {p.margin_percent}%
                    </Badge>
                  ) : p.margin_percent < p.margin_threshold_percent + 5 ? (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`product-margin-warn-${p.id}`}>{p.margin_percent}%</Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`product-margin-ok-${p.id}`}>{p.margin_percent}%</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {p.low_stock ? <Badge variant="destructive">{p.stock}</Badge> : <span>{p.stock}</span>}
                </TableCell>
                <TableCell className="text-right">
                  {canEdit && <>
                    <Button variant="ghost" size="sm" onClick={() => setBatchFor(p)} data-testid={`product-batches-${p.id}`}><Boxes className="w-4 h-4 mr-1" />Batches</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setEdit(p); setOpen(true); }} data-testid={`product-edit-${p.id}`}>Edit</Button>
                    {canDelete && <Button variant="ghost" size="sm" onClick={() => del(p)} data-testid={`product-delete-${p.id}`} className="text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>}
                  </>}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No products found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <ProductDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEdit(null); }} brands={brands} cats={cats} item={edit} onSubmit={save} />
      <BatchDialog open={!!batchFor} onClose={() => { setBatchFor(null); load(); }} product={batchFor} />
      <ImportDialog open={importOpen} onClose={(refresh) => {
        setImportOpen(false);
        if (refresh) { load(); api.get("/brands").then(r => setBrands(r.data)); api.get("/categories").then(r => setCats(r.data)); }
      }} />
    </div>
  );
}

function ImportDialog({ open, onClose }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [stage, setStage] = useState("idle"); // idle | preview | done

  const reset = () => { setFile(null); setResult(null); setStage("idle"); };

  const run = async (dry) => {
    if (!file) { toast.error("Choose an .xlsx file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/products/import-excel?dry_run=${dry ? "true" : "false"}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setStage(dry ? "preview" : "done");
      if (!dry) toast.success(`Imported ${data.created_products} new, updated ${data.updated_products}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(stage === "done"); } }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-primary" />Import products from Excel
          </DialogTitle>
        </DialogHeader>

        {stage !== "done" && (
          <div className="space-y-4">
            <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-sm space-y-2">
              <div className="font-semibold">Expected columns</div>
              <div className="text-xs text-muted-foreground font-mono leading-relaxed">
                Product Name · Brand · Category · HSN/SAC · Barcode (UPC/EAN) · GST % · MRP (₹) · Sale Price (₹) · Cost Price (₹) · Units (Qty) · Description
              </div>
              <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-0.5 mt-2">
                <li>Only <strong>Product Name</strong> is required — other columns are optional.</li>
                <li><strong>Brands & Categories</strong> are auto-created from the values in those columns.</li>
                <li>An opening-stock <strong>batch</strong> is created when <em>Units (Qty)</em> + a cost basis are present (only on first import — re-imports never duplicate stock).</li>
                <li>Re-uploading the same file <strong>updates</strong> existing products (matched by barcode, falling back to name).</li>
              </ul>
            </div>

            <div>
              <Label>Excel file (.xlsx)</Label>
              <Input
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setStage("idle"); }}
                data-testid="import-file-input"
                className="mt-1.5"
              />
              {file && <div className="text-xs text-muted-foreground mt-1.5">Selected: <span className="font-mono">{file.name}</span> ({Math.round(file.size / 1024)} KB)</div>}
            </div>

            {result && stage === "preview" && (
              <div className="space-y-3" data-testid="import-preview">
                <div className="text-sm font-semibold">Dry-run preview</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Stat label="Rows" value={result.total_rows} />
                  <Stat label="New products" value={result.created_products} tone="primary" />
                  <Stat label="Updates" value={result.updated_products} />
                  <Stat label="New batches" value={result.created_batches} />
                  <Stat label="New brands" value={result.created_brands} />
                  <Stat label="New categories" value={result.created_categories} />
                  <Stat label="Skipped" value={result.skipped} />
                  <Stat label="Errors" value={result.errors?.length || 0} tone={result.errors?.length ? "destructive" : "default"} />
                </div>
                {result.preview?.length > 0 && (
                  <div className="rounded-md border border-border overflow-hidden text-xs">
                    <div className="bg-muted/50 px-3 py-1.5 font-semibold uppercase tracking-wider text-[10px]">Sample rows</div>
                    <div className="max-h-40 overflow-y-auto divide-y divide-border">
                      {result.preview.slice(0, 5).map((row, i) => (
                        <div key={i} className="px-3 py-1.5 flex items-center justify-between gap-2">
                          <span className="truncate">{row.name}</span>
                          <span className="font-mono text-muted-foreground shrink-0">
                            {row.brand || "—"} · MRP ₹{row.mrp ?? "—"} · qty {row.qty ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {result.errors?.length > 0 && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
                    <div className="font-semibold text-destructive mb-1">{result.errors.length} errors</div>
                    <ul className="space-y-0.5 max-h-24 overflow-y-auto">
                      {result.errors.slice(0, 8).map((er, i) => (
                        <li key={i}>Row {er.row}: {er.name} — {er.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {stage === "done" && result && (
          <div className="space-y-4" data-testid="import-result">
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-sm flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-emerald-900">Import complete</div>
                <div className="text-emerald-800 text-xs mt-0.5">
                  Created {result.created_products} new products, updated {result.updated_products}, opened {result.created_batches} stock batches.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Total rows" value={result.total_rows} />
              <Stat label="New products" value={result.created_products} tone="primary" />
              <Stat label="Updated" value={result.updated_products} />
              <Stat label="Batches" value={result.created_batches} />
              <Stat label="New brands" value={result.created_brands} />
              <Stat label="New categories" value={result.created_categories} />
              <Stat label="Skipped" value={result.skipped} />
              <Stat label="Errors" value={result.errors?.length || 0} tone={result.errors?.length ? "destructive" : "default"} />
            </div>
            {result.errors?.length > 0 && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-destructive">{result.errors.length} rows failed</div>
                  <ul className="space-y-0.5 max-h-24 overflow-y-auto mt-1">
                    {result.errors.slice(0, 8).map((er, i) => (<li key={i}>Row {er.row}: {er.name} — {er.error}</li>))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {stage === "done" ? (
            <Button onClick={() => { reset(); onClose(true); }} data-testid="import-close">Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => { reset(); onClose(false); }} disabled={busy}>Cancel</Button>
              <Button variant="outline" onClick={() => run(true)} disabled={!file || busy} data-testid="import-dry-run">
                {busy && stage !== "preview" ? "Checking…" : "Preview (dry-run)"}
              </Button>
              <Button onClick={() => run(false)} disabled={!file || busy} data-testid="import-go">
                <Upload className="w-4 h-4 mr-1" />{busy ? "Importing…" : `Import ${stage === "preview" ? `(${result?.total_rows} rows)` : ""}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone = "default" }) {
  const toneCls = tone === "primary"
    ? "border-primary/40 bg-primary/5 text-primary"
    : tone === "destructive"
    ? "border-destructive/40 bg-destructive/5 text-destructive"
    : "border-border bg-muted/30";
  return (
    <div className={`rounded-md border px-3 py-2 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-heading text-xl font-bold leading-none mt-1">{value ?? 0}</div>
    </div>
  );
}

function ProductDialog({ open, onOpenChange, brands, cats, item, onSubmit }) {
  const [f, setF] = useState({});
  useEffect(() => {
    setF(item ? {
      name: item.name, sku: item.sku || "", barcode: item.barcode || "",
      brand_id: item.brand_id || "", category_id: item.category_id || "",
      hsn_code: item.hsn_code || "", gst_percent: item.gst_percent, unit: item.unit,
      mrp: item.mrp, sale_price: item.sale_price, cost_price: item.cost_price || 0,
      low_stock_threshold: item.low_stock_threshold,
      description: item.description || "",
      include_in_merchant_feed: item.include_in_merchant_feed !== false,
    } : {
      name: "", sku: "", barcode: "", brand_id: "", category_id: "", hsn_code: "",
      gst_percent: 18, unit: "pcs", mrp: 0, sale_price: 0, cost_price: 0, low_stock_threshold: 5,
      description: "",
      include_in_merchant_feed: true,
    });
  }, [item, open]);

  const change = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{item ? "Edit" : "Add"} product</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Name *"><Input value={f.name || ""} onChange={e => change("name", e.target.value)} data-testid="product-name" /></Field>
          <Field label="Barcode"><Input value={f.barcode || ""} onChange={e => change("barcode", e.target.value)} data-testid="product-barcode" /></Field>
          <Field label="SKU"><Input value={f.sku || ""} onChange={e => change("sku", e.target.value)} /></Field>
          <Field label="HSN code"><Input value={f.hsn_code || ""} onChange={e => change("hsn_code", e.target.value)} /></Field>
          <Field label="Brand">
            <Select value={f.brand_id || "_none"} onValueChange={v => change("brand_id", v === "_none" ? null : v)}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="_none">None</SelectItem>{brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Category">
            <Select value={f.category_id || "_none"} onValueChange={v => change("category_id", v === "_none" ? null : v)}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="_none">None</SelectItem>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Unit"><Input value={f.unit || "pcs"} onChange={e => change("unit", e.target.value)} /></Field>
          <Field label="GST %"><Input type="number" value={f.gst_percent ?? 18} onChange={e => change("gst_percent", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="MRP"><Input type="number" value={f.mrp ?? 0} onChange={e => change("mrp", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Sale price *"><Input type="number" value={f.sale_price ?? 0} onChange={e => change("sale_price", parseFloat(e.target.value) || 0)} data-testid="product-price" /></Field>
          <Field label="Cost price"><Input type="number" value={f.cost_price ?? 0} onChange={e => change("cost_price", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Low-stock threshold"><Input type="number" value={f.low_stock_threshold ?? 5} onChange={e => change("low_stock_threshold", parseInt(e.target.value) || 0)} /></Field>
          <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Input value={f.description || ""} onChange={e => change("description", e.target.value)} placeholder="Used in Google Merchant feed & product page" /></div>
          <div className="sm:col-span-2 flex items-center justify-between rounded-md border border-border p-3 bg-muted/30">
            <div>
              <div className="text-sm font-medium">Include in Google Merchant feed</div>
              <div className="text-xs text-muted-foreground">Appears at <code className="bg-muted px-1 rounded">/shop/&lt;id&gt;</code> + the XML feed.</div>
            </div>
            <input
              type="checkbox"
              checked={!!f.include_in_merchant_feed}
              onChange={e => change("include_in_merchant_feed", e.target.checked)}
              data-testid="product-merchant-toggle"
              className="w-4 h-4"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit(f)} data-testid="save-product"><Plus className="w-4 h-4 mr-1" />Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>
);

function BatchDialog({ open, onClose, product }) {
  const [batches, setBatches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ batch_no: "", expiry_date: "", quantity: 0, cost_price: 0, supplier_id: "" });

  useEffect(() => {
    if (!product) return;
    api.get("/batches", { params: { product_id: product.id } }).then(r => setBatches(r.data));
    api.get("/suppliers").then(r => setSuppliers(r.data));
  }, [product]);

  const add = async () => {
    if (!form.batch_no || form.quantity <= 0) { toast.error("Batch no and qty required"); return; }
    try {
      await api.post("/batches", { ...form, product_id: product.id, supplier_id: form.supplier_id || null, expiry_date: form.expiry_date || null });
      toast.success("Batch added");
      setForm({ batch_no: "", expiry_date: "", quantity: 0, cost_price: 0, supplier_id: "" });
      const r = await api.get("/batches", { params: { product_id: product.id } });
      setBatches(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="batch-dialog">
        <DialogHeader><DialogTitle>Batches · {product?.name}</DialogTitle></DialogHeader>
        <Table>
          <TableHeader><TableRow><TableHead>Batch</TableHead><TableHead>Expiry</TableHead><TableHead className="text-right">Qty</TableHead></TableRow></TableHeader>
          <TableBody>
            {batches.map(b => (
              <TableRow key={b.id}><TableCell className="font-mono text-xs">{b.batch_no}</TableCell><TableCell>{b.expiry_date || "—"}</TableCell><TableCell className="text-right">{b.quantity}</TableCell></TableRow>
            ))}
            {batches.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-4">No batches.</TableCell></TableRow>}
          </TableBody>
        </Table>
        <div className="border-t border-border pt-3 grid sm:grid-cols-5 gap-2 items-end">
          <Field label="Batch #"><Input value={form.batch_no} onChange={e => setForm(f => ({ ...f, batch_no: e.target.value }))} data-testid="batch-no" /></Field>
          <Field label="Expiry"><Input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} data-testid="batch-expiry" /></Field>
          <Field label="Quantity"><Input type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: parseInt(e.target.value) || 0 }))} data-testid="batch-qty" /></Field>
          <Field label="Cost"><Input type="number" value={form.cost_price} onChange={e => setForm(f => ({ ...f, cost_price: parseFloat(e.target.value) || 0 }))} /></Field>
          <Button onClick={add} className="mb-0.5" data-testid="add-batch-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
